// Gemeinsame Helfer der Vereinsverwaltung (Liste, Seitenleiste, Vereinsseite).
// Muster: ~/Documents/TDA-Muster/Muster_Vereine_Verwaltung.html, Variante A (22.10.2026).

/** Anfrage mit Anmeldung. Wirft bei jeder Antwort ausser 2xx, mit der Meldung des Servers. */
export async function apiJson(url, optionen = {}) {
  const token = localStorage.getItem('token');
  const kopf = { ...(optionen.body ? { 'Content-Type': 'application/json' } : {}), ...(optionen.headers || {}) };
  if (token) kopf.Authorization = `Bearer ${token}`;
  let res;
  try {
    res = await fetch(url, { ...optionen, headers: kopf });
  } catch (e) {
    const f = new Error('Keine Verbindung zum Server');
    f.technisch = `${optionen.method || 'GET'} ${url} · ${e.message}`;
    throw f;
  }
  let daten = null;
  try { daten = await res.json(); } catch { /* keine JSON-Antwort */ }
  if (!res.ok || (daten && daten.success === false)) {
    const f = new Error(daten?.error || daten?.message || `Fehler ${res.status}`);
    f.status = res.status;
    f.felder = daten?.felder || null;
    f.technisch = `${optionen.method || 'GET'} ${url} · ${res.status}`;
    throw f;
  }
  return daten;
}

/** Nur echte Vereinskonten zählen als Verein; Admin-Konten heissen hier Verwaltungskonto. */
export const istVerein = (v) => (v?.rolle || 'verein') === 'verein';
/** Platzhalter „Ohne Verein“ (backend utils/ohneVerein.js) ist kein Konto und erscheint nirgends. */
export const istPlatzhalter = (v) => v?.rolle === 'ohne_verein';

const TAG = 864e5;
export function tageSeit(d) {
  if (!d) return null;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - t.getTime()) / TAG));
}

export function datum(d) {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  return t.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Login vor wie vielen Tagen, in Worten. */
export function loginRelativ(d) {
  const t = tageSeit(d);
  if (t === null) return '';
  if (t === 0) return 'heute';
  if (t === 1) return 'gestern';
  if (t < 31) return `vor ${t} Tagen`;
  if (t < 365) { const m = Math.round(t / 30); return m === 1 ? 'vor einem Monat' : `vor ${m} Monaten`; }
  return 'vor über einem Jahr';
}

export const AKTIV_TAGE = 60;

/** [schlüssel, Text] für das Status-Abzeichen. */
export function statusVon(v) {
  if (v?.archiv_id) return ['archiv', 'Archiviert'];
  if (v?.status === 'gesperrt') return ['gesperrt', 'Gesperrt'];
  if (!v?.letzter_login) return ['nie', 'Nie angemeldet'];
  if (tageSeit(v.letzter_login) <= AKTIV_TAGE) return ['aktiv', 'Aktiv'];
  return ['ruhig', 'Ruhig'];
}

export function initialen(name) {
  const w = String(name || '').replace(/e\.\s?V\./gi, '').split(/\s+/)
    .filter((x) => x && !/^(der|die|das|und|&|-)$/i.test(x));
  return ((w[0]?.[0] || '') + (w[1]?.[0] || '')).toUpperCase() || '?';
}

/** Orte in GROSSBUCHSTABEN (so eingetragen) normal schreiben, sonst unverändert. */
export function ortText(ort) {
  const o = String(ort || '').trim();
  if (o.length > 3 && o === o.toUpperCase() && /[A-ZÄÖÜ]/.test(o)) return o.charAt(0) + o.slice(1).toLowerCase();
  return o;
}

export const plzOrt = (v) => [v?.plz, ortText(v?.ort)].filter(Boolean).join(' ');
export const anschrift = (v) => [[v?.strasse, v?.hausnummer].filter(Boolean).join(' '), plzOrt(v)].filter(Boolean).join(', ');

/** Prüft die Vereinsdaten wie der Server (controllers/vereineController.js pruefeVereinsdaten). */
export function pruefeVereinsdaten(d) {
  const f = {};
  if (!String(d.name || '').trim()) f.name = 'Name fehlt';
  const mail = String(d.email || '').trim();
  if (!mail) f.email = 'E-Mail fehlt, sie ist die Anmeldung des Vereins';
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail)) f.email = 'Das ist keine gültige E-Mail-Adresse';
  const plz = String(d.plz || '').trim();
  if (plz && !/^\d{4,5}$/.test(plz)) f.plz = 'PLZ hat 4 oder 5 Ziffern';
  if (String(d.telefon || '').trim().length > 20) f.telefon = 'Höchstens 20 Zeichen';
  if (String(d.hausnummer || '').trim().length > 10) f.hausnummer = 'Höchstens 10 Zeichen';
  const hp = String(d.homepage || '').trim();
  if (hp && /\s/.test(hp)) f.homepage = 'Die Adresse darf keine Leerzeichen enthalten';
  return f;
}

/** Alter am heutigen Tag aus dem Geburtsdatum (YYYY-MM-DD…). */
export function alterAus(geb) {
  if (!geb) return null;
  const s = String(geb).slice(0, 10);
  const [j, m, t] = s.split('-').map(Number);
  if (!j) return null;
  const h = new Date();
  let a = h.getFullYear() - j;
  if (h.getMonth() + 1 < m || (h.getMonth() + 1 === m && h.getDate() < t)) a -= 1;
  return a;
}
export const jahrgang = (geb) => (geb ? Number(String(geb).slice(0, 4)) || null : null);

/** Gurtfarben für das kleine Farbfeld. Unbekannte bleiben grau. */
const GURT = {
  'weiß': '#f5f5f4', gelb: '#facc15', orange: '#fb923c', 'grün': '#16a34a', blau: '#2563eb',
  lila: '#7c3aed', rot: '#dc2626', braun: '#7c4a1e', schwarz: '#111111',
};
export function gurtHintergrund(g) {
  // „Schwarzgurt“, „Gelbgurt 2. Kyu“: die erste bekannte Farbe im Teil zählt
  const farbe = (x) => GURT[x.trim()] || Object.entries(GURT).find(([k]) => x.includes(k))?.[1];
  const teile = String(g || '').toLowerCase().split('/').map(farbe).filter(Boolean);
  if (!teile.length) return '#c8c3bd';
  if (teile.length === 1) return teile[0];
  return `linear-gradient(90deg, ${teile[0]} 50%, ${teile[1]} 50%)`;
}

export const istStorniert = (a) => a?.status === 'Storniert' || a?.status === 'Abgemeldet';
